/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { Button, Input , InputGroup, InputGroupText } from 'reactstrap';
import { Portfolio } from '@g17eco/types/portfolio';
import { addHoldingToPortfolio } from '@actions/portfolio-tracker';
import { InitiativeSearch } from '@features/search/InitiativeSearch';
import { searchInitiativeHoldings } from '@actions/initiative';
import { Loader } from '@g17eco/atoms/loader';
import { useSelector } from 'react-redux';
import { getInitiativeGroupFlatTree } from '@selectors/initiativeTree';
import { InitiativePlain } from '@g17eco/types/initiative';
import { Suggestion } from '@g17eco/types/search';
import { DataShareModal, getDefaultContent } from '@features/data-share/DataShareModal';
import { RequesterType } from '@g17eco/types/dataShare';

const initialSuggestion = { _id: '', name: '' };

const addParents = (itemId: string, initiativeTree: InitiativePlain[], filteredIds: string[]): void => {
  const item = initiativeTree.find((i) => i._id === itemId);
  if (!item || !item.parentId) {
    return;
  }

  filteredIds.push(item.parentId);
  addParents(item.parentId, initiativeTree, filteredIds);
};

export const AddHoldingForm = (props: { portfolio: Portfolio; handleReload: () => void }) => {
  const { portfolio, handleReload } = props;

  const [weight, setWeight] = React.useState<number | undefined>(undefined);
  const [suggestion, setSuggestion] = React.useState(initialSuggestion);
  const initiativeTree = useSelector(getInitiativeGroupFlatTree);
  const [openDataShareRequestModal, setDataShareRequestModal] = useState(false);

  const [errorMessage, setMessage] = React.useState('');
  const [isSaving, setSaving] = React.useState(false);

  const handleCloseModal = () => {
    setSuggestion(initialSuggestion);
    setDataShareRequestModal(false);
    handleReload();
  };

  const handleSetHolding = (value: string, suggestion?: Suggestion) => {
    if (suggestion && suggestion._id) {
      setSuggestion(suggestion);
    } else {
      setSuggestion(initialSuggestion);
    }
  };

  const canSubmit = suggestion._id && weight !== undefined;

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setMessage('');
    if (isSaving) {
      return;
    }
    setSaving(true);
    try {
      await addHoldingToPortfolio(portfolio._id, suggestion._id, weight ?? 0);
      setDataShareRequestModal(true);
    } catch (e) {
      setMessage(e.message);
    } finally {
      setWeight(undefined);
      setSaving(false);
    }
  };

  const filteredIds = [portfolio._id, ...portfolio.scorecard.children.map((c) => c.initiativeId)];
  addParents(portfolio._id, initiativeTree, filteredIds);

  if (isSaving) {
    return <Loader relative={true} />;
  }

  return (
    <>
      <h5>
        <i className='far fa-building mr-2' />
        Add a new company holding
      </h5>
      {errorMessage && <div className='alert alert-danger'>{errorMessage}</div>}
      <div className='mt-3 ml-4'>
        <InitiativeSearch
          disabled={isSaving}
          placeholder={'Search for a company'}
          searchInitiative={searchInitiativeHoldings}
          filterOptions={filteredIds}
          handleValueChange={handleSetHolding}
        />
      </div>
      <div className='mt-2 ml-4 d-flex justify-content-between'>
        <div className='pr-2'>
          <InputGroup>
            <Input
              min={0}
              type='number'
              disabled={isSaving}
              placeholder={'Holding weight'}
              defaultValue={weight}
              onChange={(e) => setWeight(Number(e.target.value))}
            />
            <InputGroupText>%</InputGroupText>
          </InputGroup>
        </div>
        <Button disabled={isSaving || !canSubmit} onClick={handleSubmit}>
          <i className='fa fa-plus mr-2' />
          Add
        </Button>
      </div>
      {openDataShareRequestModal && suggestion._id ? (
        <DataShareModal
          isOpen={openDataShareRequestModal}
          toggle={handleCloseModal}
          requesterId={portfolio._id}
          requesterType={RequesterType.Portfolio}
          title={`${portfolio.name} is requesting access to ${suggestion.name}`}
          content={getDefaultContent({ requesterName: portfolio.name })}
          initiativeId={suggestion._id}
          initiativeName={suggestion.name}
        />
      ) : null}
    </>
  );
};
